import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTb360BidEngagements, type Tb360BidEngagementEntry } from "../lib/api";
import { relativeFromNow } from "../lib/format";
import { useDocumentTitle } from "../hooks/use-document-title";
import { formatAmount, OUTCOME_LABELS, outcomeBadge } from "./OutbidEngagementPage";

const PAGE_SIZE = 50;

export function Tb360BidEngagementTable({
  items,
  showDealer,
  onSelect,
}: {
  items: Tb360BidEngagementEntry[];
  showDealer: boolean;
  onSelect: (engagement: Tb360BidEngagementEntry) => void;
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>Tapped at</th>
          <th>Vehicle</th>
          <th>Project ID</th>
          {showDealer && <th>Dealer</th>}
          <th>Bid amount</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="clickable-row" onClick={() => onSelect(item)}>
            <td className="mono" title={new Date(item.tappedAt).toLocaleString()}>
              {relativeFromNow(item.tappedAt)}
            </td>
            <td>
              <div>{item.vehicleTitle ?? `Project ${item.projectId}`}</div>
              {item.vehicleVin && (
                <div className="mono" style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {item.vehicleVin}
                </div>
              )}
            </td>
            <td className="mono">{item.projectId}</td>
            {showDealer && (
              <td onClick={(e) => e.stopPropagation()}>
                <Link to={`/dealer/${item.dealerId}`} className="dealer-link">
                  {item.dealerName ?? `#${item.dealerId}`}
                </Link>
              </td>
            )}
            <td className="mono">{formatAmount(item.bidAmount, item.currency)}</td>
            <td>
              <span className={outcomeBadge(item.outcome)}>{OUTCOME_LABELS[item.outcome]}</span>
              {item.unsuccessfulReason && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {item.unsuccessfulReason}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Tb360BidEngagementPage() {
  useDocumentTitle("TB360 Bid Engagement");
  const [filterDealer, setFilterDealer] = useState("");
  const [items, setItems] = useState<Tb360BidEngagementEntry[]>([]);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Tb360BidEngagementEntry | null>(null);

  const loadPage = useCallback(
    async (cursor: string | undefined) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchTb360BidEngagements({
          dealerId: filterDealer || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        setItems(result.items);
        setNextCursor(result.nextCursor);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    },
    [filterDealer],
  );

  useEffect(() => {
    setPageIndex(0);
    setCursors([null]);
    void loadPage(undefined);
  }, [loadPage]);

  function handleNext() {
    if (!nextCursor) return;
    const newPageIndex = pageIndex + 1;
    const newCursors = [...cursors];
    if (newCursors.length <= newPageIndex) {
      newCursors.push(nextCursor);
    }
    setCursors(newCursors);
    setPageIndex(newPageIndex);
    void loadPage(nextCursor);
  }

  function handlePrev() {
    if (pageIndex <= 0) return;
    const newPageIndex = pageIndex - 1;
    setPageIndex(newPageIndex);
    void loadPage(cursors[newPageIndex] ?? undefined);
  }

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">TB360 Bid Engagement</h1>
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => {
            setPageIndex(0);
            setCursors([null]);
            void loadPage(undefined);
          }}
        >
          Refresh
        </button>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Filter by dealer ID"
          value={filterDealer}
          onChange={(e) => setFilterDealer(e.target.value)}
          style={{ width: 160 }}
        />
      </div>

      {loading && <div className="loading">Loading TB360 bid engagements...</div>}
      {error && <div className="error-msg">{error}</div>}

      {!loading && items.length === 0 && (
        <div className="empty-state">No TB360 bid engagements found</div>
      )}

      {items.length > 0 && (
        <>
          <Tb360BidEngagementTable items={items} showDealer onSelect={setSelected} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 13,
            }}
          >
            <button
              className="btn btn-sm"
              type="button"
              onClick={handlePrev}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span style={{ color: "var(--color-text-secondary)" }}>
              Page {pageIndex + 1}
            </span>
            <button
              className="btn btn-sm"
              type="button"
              onClick={handleNext}
              disabled={!nextCursor}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selected && (
        <Tb360BidEngagementDrawer engagement={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

export function Tb360BidEngagementDrawer({
  engagement,
  onClose,
}: {
  engagement: Tb360BidEngagementEntry;
  onClose: () => void;
}) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div>
            <div className="drawer-title">
              {engagement.vehicleTitle ?? `Project ${engagement.projectId}`}
            </div>
            <div className="drawer-subtitle mono">
              {new Date(engagement.respondedAt ?? engagement.tappedAt).toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-section">
            <div className="drawer-section-title">Summary</div>
            <div className="drawer-summary-grid">
              <div className="drawer-summary-label">Status</div>
              <div>
                <span className={outcomeBadge(engagement.outcome)}>
                  {OUTCOME_LABELS[engagement.outcome]}
                </span>
                {engagement.unsuccessfulReason && (
                  <span className="drawer-outcome-reason">{engagement.unsuccessfulReason}</span>
                )}
              </div>
              {engagement.errorMessage && (
                <>
                  <div className="drawer-summary-label">Error</div>
                  <div className="drawer-mono-wrap">{engagement.errorMessage}</div>
                </>
              )}
              <div className="drawer-summary-label">Dealer</div>
              <div>
                <Link to={`/dealer/${engagement.dealerId}`} className="dealer-link">
                  {engagement.dealerName ?? `#${engagement.dealerId}`}
                </Link>
              </div>
              <div className="drawer-summary-label">Project ID</div>
              <div className="mono">{engagement.projectId}</div>
              <div className="drawer-summary-label">VIN</div>
              <div className="mono">{engagement.vehicleVin ?? "—"}</div>
              <div className="drawer-summary-label">Tapped at</div>
              <div className="mono">{new Date(engagement.tappedAt).toLocaleString()}</div>
              <div className="drawer-summary-label">Last step at</div>
              <div className="mono">
                {engagement.respondedAt ? new Date(engagement.respondedAt).toLocaleString() : "—"}
              </div>
              <div className="drawer-summary-label">Bid amount</div>
              <div className="mono">{formatAmount(engagement.bidAmount, engagement.currency)}</div>
              <div className="drawer-summary-label">Tap event ID</div>
              <div className="mono drawer-mono-wrap">{engagement.id}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
